import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  ChevronDown, 
  ChevronRight, 
  ChevronUp,
  GripVertical, 
  Trash2, 
  Edit, 
  Plus, 
  Save, 
  X,
  AlertTriangle,
  Package,
  Copy,
  ShoppingBag
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sortable Item Component
const SortableItem = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div className="flex items-center gap-2">
        <button {...listeners} className="cursor-grab hover:bg-slate-100 p-1 rounded">
          <GripVertical className="w-4 h-4 text-slate-400" />
        </button>
        {children}
      </div>
    </div>
  );
};

// Confirmation Dialog
const ConfirmDialog = ({ open, onClose, onConfirm, title, message }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-red-600">
          <AlertTriangle className="w-5 h-5" />
          {title}
        </DialogTitle>
      </DialogHeader>
      <p className="text-slate-600 py-4">{message}</p>
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={onClose}>Anulează</Button>
        <Button variant="destructive" onClick={onConfirm}>Șterge</Button>
      </div>
    </DialogContent>
  </Dialog>
);

// Material Editor Component
const MaterialEditor = ({ material, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: material?.name || '',
    price_per_unit: material?.price_per_unit || 0,
    labor_per_unit: material?.labor_per_unit || 0,
    unit: material?.unit || 'mp',
    category: material?.category || '',
  });

  return (
    <div className="bg-slate-50 p-4 rounded-lg space-y-3">
      <div>
        <Label>Nume Material</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Nume material"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Preț Material (LEI)</Label>
          <Input
            type="number"
            step="0.01"
            value={formData.price_per_unit}
            onChange={(e) => setFormData({ ...formData, price_per_unit: parseFloat(e.target.value) || 0 })}
          />
        </div>
        <div>
          <Label>Manoperă (LEI)</Label>
          <Input
            type="number"
            step="0.01"
            value={formData.labor_per_unit}
            onChange={(e) => setFormData({ ...formData, labor_per_unit: parseFloat(e.target.value) || 0 })}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Unitate</Label>
          <Input
            value={formData.unit}
            onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
          />
        </div>
        <div>
          <Label>Categorie</Label>
          <Input
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" size="sm" onClick={onCancel}>
          <X className="w-4 h-4 mr-1" /> Anulează
        </Button>
        <Button size="sm" className="btn-primary" onClick={() => onSave(formData)}>
          <Save className="w-4 h-4 mr-1" /> Salvează
        </Button>
      </div>
    </div>
  );
};

// Product to Material Dialog
const ProductToMaterialDialog = ({ open, onClose, phase, roofType, token, onSuccess }) => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    labor_per_unit: 0,
    unit: 'mp',
    custom_name: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchProducts();
    }
  }, [open]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleSubmit = async () => {
    if (!selectedProduct) {
      toast.error('Selectați un produs');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/admin/product-to-material`,
        {
          product_id: selectedProduct.id,
          phase_id: phase.id,
          roof_type_id: roofType.id,
          labor_per_unit: formData.labor_per_unit,
          unit: formData.unit,
          custom_name: formData.custom_name || selectedProduct.name
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Produs transformat în material');
      onSuccess();
      onClose();
      setSelectedProduct(null);
      setFormData({ labor_per_unit: 0, unit: 'mp', custom_name: '' });
    } catch (error) {
      console.error('Error converting product:', error);
      toast.error('Eroare la conversie');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-orange-600" />
            Transformă Produs din Magazin în Material
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <p className="text-sm text-slate-600">
            Selectează un produs din magazin pentru a-l adăuga ca material la faza "{phase?.name}"
          </p>
          
          <div>
            <Label>Selectează Produs *</Label>
            <Select
              value={selectedProduct?.id || ''}
              onValueChange={(value) => {
                const product = products.find(p => p.id === value);
                setSelectedProduct(product);
                if (product) {
                  setFormData(prev => ({
                    ...prev,
                    custom_name: product.name,
                    unit: product.unit || 'mp'
                  }));
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Alege un produs" />
              </SelectTrigger>
              <SelectContent>
                {products.map(product => (
                  <SelectItem key={product.id} value={product.id}>
                    {product.name} - {product.price.toFixed(2)} LEI/{product.unit}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedProduct && (
            <>
              <div className="bg-orange-50 p-3 rounded-lg">
                <p className="text-sm"><strong>Produs selectat:</strong> {selectedProduct.name}</p>
                <p className="text-sm"><strong>Preț material:</strong> {selectedProduct.price.toFixed(2)} LEI</p>
              </div>

              <div>
                <Label>Nume personalizat (opțional)</Label>
                <Input
                  value={formData.custom_name}
                  onChange={(e) => setFormData({ ...formData, custom_name: e.target.value })}
                  placeholder={selectedProduct.name}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Preț Manoperă (LEI) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.labor_per_unit}
                    onChange={(e) => setFormData({ ...formData, labor_per_unit: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label>Unitate de măsură</Label>
                  <Select
                    value={formData.unit}
                    onValueChange={(value) => setFormData({ ...formData, unit: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mp">mp</SelectItem>
                      <SelectItem value="ml">ml</SelectItem>
                      <SelectItem value="buc">buc</SelectItem>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="l">l</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
        </div>
        
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Anulează</Button>
          <Button onClick={handleSubmit} disabled={loading || !selectedProduct} className="btn-primary">
            {loading ? 'Se adaugă...' : 'Adaugă Material'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Phase Card Component
const PhaseCard = ({ phase, token, roofType, onUpdate, onDelete, expanded, onToggleExpand }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    name: phase.name,
    description: phase.description,
    order: phase.order,
  });
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [showAddMaterial, setShowAddMaterial] = useState(false);
  const [showProductToMaterial, setShowProductToMaterial] = useState(false);

  const handleSavePhase = async () => {
    try {
      await axios.put(
        `${API}/admin/work-phases/${phase.id}`,
        editData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Fază actualizată');
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      console.error('Error updating phase:', error);
      toast.error('Eroare la actualizare');
    }
  };

  const handleSaveMaterial = async (materialData) => {
    try {
      if (editingMaterial) {
        await axios.put(
          `${API}/admin/materials/${editingMaterial.id}`,
          {
            ...materialData,
            roof_type_id: phase.roof_type_id,
            phase_id: phase.id,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Material actualizat');
      }
      setEditingMaterial(null);
      onUpdate();
    } catch (error) {
      console.error('Error saving material:', error);
      toast.error('Eroare la salvare');
    }
  };

  const handleAddMaterial = async (materialData) => {
    try {
      await axios.post(
        `${API}/admin/materials/add-to-phase`,
        {
          ...materialData,
          roof_type_id: phase.roof_type_id,
          phase_id: phase.id,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Material adăugat');
      setShowAddMaterial(false);
      onUpdate();
    } catch (error) {
      console.error('Error adding material:', error);
      toast.error('Eroare la adăugare');
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    if (!window.confirm('Sigur doriți să ștergeți acest material?')) return;
    
    try {
      await axios.delete(
        `${API}/admin/materials/${materialId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Material șters');
      onUpdate();
    } catch (error) {
      console.error('Error deleting material:', error);
      toast.error('Eroare la ștergere');
    }
  };

  const handleDuplicateMaterial = async (materialId) => {
    try {
      const response = await axios.post(
        `${API}/admin/materials/${materialId}/duplicate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Material duplicat: ${response.data.name}`);
      onUpdate();
    } catch (error) {
      console.error('Error duplicating material:', error);
      toast.error('Eroare la duplicare');
    }
  };

  return (
    <div className="border rounded-lg bg-white overflow-hidden" data-testid={`phase-card-${phase.id}`}>
      <div 
        className="flex items-center justify-between p-4 bg-slate-50 cursor-pointer hover:bg-slate-100"
        onClick={onToggleExpand}
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <div>
            <h4 className="font-medium text-slate-900">
              {phase.order}. {phase.name}
            </h4>
            <p className="text-sm text-slate-500">{phase.materials?.length || 0} materiale</p>
          </div>
        </div>
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => setIsEditing(true)}
          >
            <Edit className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-red-500 hover:text-red-700"
            onClick={() => onDelete(phase.id)}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="p-4 border-t space-y-4">
          {isEditing ? (
            <div className="bg-cyan-50 p-4 rounded-lg space-y-3">
              <div>
                <Label>Nume Fază</Label>
                <Input
                  value={editData.name}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Descriere</Label>
                <Textarea
                  value={editData.description}
                  onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                  rows={2}
                />
              </div>
              <div>
                <Label>Ordine</Label>
                <Input
                  type="number"
                  value={editData.order}
                  onChange={(e) => setEditData({ ...editData, order: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setIsEditing(false)}>
                  Anulează
                </Button>
                <Button size="sm" className="btn-primary" onClick={handleSavePhase}>
                  <Save className="w-4 h-4 mr-1" /> Salvează
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-600 italic">{phase.description}</p>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h5 className="font-medium text-slate-700 text-sm">Materiale disponibile:</h5>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowProductToMaterial(true)}
                  className="text-orange-600 border-orange-300 hover:bg-orange-50"
                >
                  <ShoppingBag className="w-4 h-4 mr-1" /> Din Magazin
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowAddMaterial(true)}
                >
                  <Plus className="w-4 h-4 mr-1" /> Adaugă Manual
                </Button>
              </div>
            </div>

            {showAddMaterial && (
              <MaterialEditor
                onSave={handleAddMaterial}
                onCancel={() => setShowAddMaterial(false)}
              />
            )}

            {phase.materials?.map((material) => (
              <div key={material.id} className="border rounded p-3 bg-white">
                {editingMaterial?.id === material.id ? (
                  <MaterialEditor
                    material={material}
                    onSave={handleSaveMaterial}
                    onCancel={() => setEditingMaterial(null)}
                  />
                ) : (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{material.name}</p>
                      <p className="text-xs text-slate-500">
                        Material: <span className="text-orange-600 font-medium">{material.price_per_unit?.toFixed(2)} LEI</span> | 
                        Manoperă: <span className="text-cyan-600 font-medium">{material.labor_per_unit?.toFixed(2)} LEI</span> / {material.unit}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDuplicateMaterial(material.id)}
                        title="Duplică"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setEditingMaterial(material)}
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-500"
                        onClick={() => handleDeleteMaterial(material.id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {(!phase.materials || phase.materials.length === 0) && (
              <p className="text-sm text-slate-400 italic text-center py-2">
                Nu există materiale pentru această fază
              </p>
            )}
          </div>
        </div>
      )}

      <ProductToMaterialDialog
        open={showProductToMaterial}
        onClose={() => setShowProductToMaterial(false)}
        phase={phase}
        roofType={roofType}
        token={token}
        onSuccess={onUpdate}
      />
    </div>
  );
};

// SubType Section Component
const SubTypeSection = ({ roofType, subType, token, onUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  const [expandedPhases, setExpandedPhases] = useState({});
  const [showAddPhase, setShowAddPhase] = useState(false);
  const [newPhase, setNewPhase] = useState({ name: '', description: '', order: 1 });
  const [confirmDelete, setConfirmDelete] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id && subType.phases) {
      const oldIndex = subType.phases.findIndex(p => p.id === active.id);
      const newIndex = subType.phases.findIndex(p => p.id === over.id);
      
      const newPhases = arrayMove(subType.phases, oldIndex, newIndex);
      
      const phasesData = newPhases.map((p, index) => ({
        id: p.id,
        order: index + 1
      }));

      try {
        await axios.put(
          `${API}/admin/work-phases/reorder`,
          { phases: phasesData },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Ordine actualizată');
        onUpdate();
      } catch (error) {
        console.error('Error reordering:', error);
        toast.error('Eroare la reordonare');
      }
    }
  };

  const handleDeletePhase = async (phaseId) => {
    try {
      await axios.delete(
        `${API}/admin/work-phases/${phaseId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Fază ștearsă');
      setConfirmDelete(null);
      onUpdate();
    } catch (error) {
      console.error('Error deleting phase:', error);
      toast.error('Eroare la ștergere');
    }
  };

  const handleAddPhase = async () => {
    if (!newPhase.name) {
      toast.error('Introduceți numele fazei');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/work-phases/${roofType.id}/${subType.name}`,
        {
          name: newPhase.name,
          description: newPhase.description,
          order: newPhase.order,
          roof_type_id: roofType.id,
          sub_type: subType.name
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Fază adăugată');
      setShowAddPhase(false);
      setNewPhase({ name: '', description: '', order: (subType.phases?.length || 0) + 1 });
      onUpdate();
    } catch (error) {
      console.error('Error adding phase:', error);
      toast.error('Eroare la adăugare');
    }
  };

  return (
    <div className="border rounded-xl overflow-hidden bg-white" data-testid={`subtype-${subType.name}`}>
      <div 
        className="flex items-center justify-between p-4 bg-gradient-to-r from-cyan-50 to-slate-50 cursor-pointer hover:from-cyan-100"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="w-5 h-5 text-cyan-600" /> : <ChevronRight className="w-5 h-5 text-cyan-600" />}
          <div>
            <h3 className="font-bold text-slate-900">{subType.name}</h3>
            <p className="text-sm text-slate-500">{subType.phases?.length || 0} etape de lucru</p>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="p-4 space-y-4">
          <div className="flex justify-end">
            <Button 
              variant="outline"
              onClick={() => {
                setShowAddPhase(true);
                setNewPhase({ ...newPhase, order: (subType.phases?.length || 0) + 1 });
              }}
            >
              <Plus className="w-4 h-4 mr-2" /> Adaugă Etapă
            </Button>
          </div>

          {showAddPhase && (
            <div className="bg-green-50 p-4 rounded-lg space-y-3 border border-green-200">
              <h4 className="font-medium text-green-800">Etapă Nouă</h4>
              <div>
                <Label>Nume Etapă</Label>
                <Input
                  value={newPhase.name}
                  onChange={(e) => setNewPhase({ ...newPhase, name: e.target.value })}
                  placeholder="ex: Aplicare grund"
                />
              </div>
              <div>
                <Label>Descriere</Label>
                <Textarea
                  value={newPhase.description}
                  onChange={(e) => setNewPhase({ ...newPhase, description: e.target.value })}
                  placeholder="Descriere etapă..."
                  rows={2}
                />
              </div>
              <div>
                <Label>Ordine</Label>
                <Input
                  type="number"
                  value={newPhase.order}
                  onChange={(e) => setNewPhase({ ...newPhase, order: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowAddPhase(false)}>
                  Anulează
                </Button>
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={handleAddPhase}>
                  <Plus className="w-4 h-4 mr-1" /> Adaugă
                </Button>
              </div>
            </div>
          )}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={subType.phases?.map(p => p.id) || []}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {subType.phases?.map((phase) => (
                  <SortableItem key={phase.id} id={phase.id}>
                    <div className="flex-1">
                      <PhaseCard
                        phase={phase}
                        token={token}
                        roofType={roofType}
                        onUpdate={onUpdate}
                        onDelete={(id) => setConfirmDelete(id)}
                        expanded={expandedPhases[phase.id]}
                        onToggleExpand={() => setExpandedPhases(prev => ({
                          ...prev,
                          [phase.id]: !prev[phase.id]
                        }))}
                      />
                    </div>
                  </SortableItem>
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {(!subType.phases || subType.phases.length === 0) && (
            <div className="text-center py-8 text-slate-400">
              <p>Nu există etape de lucru pentru acest subtip.</p>
              <p className="text-sm">Adaugă prima etapă folosind butonul de sus.</p>
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => handleDeletePhase(confirmDelete)}
        title="Șterge Etapa"
        message="Sigur doriți să ștergeți această etapă? Toate materialele asociate vor fi de asemenea șterse."
      />
    </div>
  );
};

// Main Calculator Manager Component
const CalculatorManager = ({ token }) => {
  const [calculators, setCalculators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRoofTypes, setExpandedRoofTypes] = useState({});
  const [confirmDeleteSubtype, setConfirmDeleteSubtype] = useState(null);
  const [confirmDeleteRoofType, setConfirmDeleteRoofType] = useState(null);
  const [showAddMaterialDialog, setShowAddMaterialDialog] = useState(null);

  useEffect(() => {
    fetchCalculators();
  }, []);

  const fetchCalculators = async () => {
    try {
      const response = await axios.get(`${API}/admin/calculators`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalculators(response.data);
    } catch (error) {
      console.error('Error fetching calculators:', error);
      toast.error('Eroare la încărcarea datelor');
    } finally {
      setLoading(false);
    }
  };

  const handleMoveRoofType = async (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= calculators.length) return;

    const newCalculators = [...calculators];
    [newCalculators[index], newCalculators[newIndex]] = [newCalculators[newIndex], newCalculators[index]];

    const roofTypesData = newCalculators.map((rt, i) => ({
      id: rt.id,
      order: i
    }));

    try {
      await axios.put(
        `${API}/admin/roof-types/reorder`,
        { roof_types: roofTypesData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ordine actualizată');
      fetchCalculators();
    } catch (error) {
      console.error('Error reordering roof types:', error);
      toast.error('Eroare la reordonare');
    }
  };

  const handleDuplicateRoofType = async (roofTypeId) => {
    try {
      const response = await axios.post(
        `${API}/admin/roof-types/${roofTypeId}/duplicate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Calculator duplicat: ${response.data.name}`);
      fetchCalculators();
    } catch (error) {
      console.error('Error duplicating roof type:', error);
      toast.error('Eroare la duplicare');
    }
  };

  const handleDeleteRoofType = async () => {
    if (!confirmDeleteRoofType) return;

    try {
      await axios.delete(
        `${API}/admin/roof-types/${confirmDeleteRoofType.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Calculator șters');
      setConfirmDeleteRoofType(null);
      fetchCalculators();
    } catch (error) {
      console.error('Error deleting roof type:', error);
      toast.error('Eroare la ștergere');
    }
  };

  const handleDeleteSubtype = async () => {
    if (!confirmDeleteSubtype) return;
    
    const { roofTypeId, subtypeName } = confirmDeleteSubtype;
    
    try {
      await axios.delete(
        `${API}/admin/roof-types/${roofTypeId}/subtypes/${encodeURIComponent(subtypeName)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Subtip șters cu succes');
      setConfirmDeleteSubtype(null);
      fetchCalculators();
    } catch (error) {
      console.error('Error deleting subtype:', error);
      toast.error('Eroare la ștergere');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calculator-manager">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Gestionare Calculatoare
        </h2>
        <p className="text-slate-600">
          Administrează tipurile de acoperiș, subtipurile, etapele de lucru și materialele
        </p>
      </div>

      {calculators.map((roofType, index) => (
        <div 
          key={roofType.id} 
          className="border-2 rounded-2xl overflow-hidden shadow-sm"
          data-testid={`roof-type-${roofType.id}`}
        >
          <div 
            className="flex items-center justify-between p-6 bg-gradient-to-r from-slate-900 to-slate-800"
          >
            <div 
              className="flex items-center gap-4 flex-1 cursor-pointer"
              onClick={() => setExpandedRoofTypes(prev => ({
                ...prev,
                [roofType.id]: !prev[roofType.id]
              }))}
            >
              {expandedRoofTypes[roofType.id] ? 
                <ChevronDown className="w-6 h-6 text-cyan-400" /> : 
                <ChevronRight className="w-6 h-6 text-cyan-400" />
              }
              <div>
                <h2 className="text-xl font-bold text-white">{roofType.name}</h2>
                <p className="text-slate-400 text-sm">{roofType.sub_types?.length || 0} subtipuri</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Move Up/Down */}
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => handleMoveRoofType(index, 'up')}
                disabled={index === 0}
                className="text-slate-300 hover:text-white hover:bg-slate-700"
              >
                <ChevronUp className="w-4 h-4" />
              </Button>
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => handleMoveRoofType(index, 'down')}
                disabled={index === calculators.length - 1}
                className="text-slate-300 hover:text-white hover:bg-slate-700"
              >
                <ChevronDown className="w-4 h-4" />
              </Button>
              {/* Duplicate */}
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => handleDuplicateRoofType(roofType.id)}
                className="text-slate-300 hover:text-white hover:bg-slate-700"
                title="Duplică calculator"
              >
                <Copy className="w-4 h-4" />
              </Button>
              {/* Delete */}
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => setConfirmDeleteRoofType(roofType)}
                className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                title="Șterge calculator"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
              {/* Add Material Global */}
              <Button 
                variant="secondary"
                size="sm"
                onClick={() => setShowAddMaterialDialog(roofType)}
              >
                <Package className="w-4 h-4 mr-2" />
                Material Global
              </Button>
            </div>
          </div>

          {expandedRoofTypes[roofType.id] && (
            <div className="p-6 bg-slate-50 space-y-4">
              {roofType.sub_types?.map((subType) => (
                <div key={subType.name} className="flex items-start gap-2">
                  <div className="flex-1">
                    <SubTypeSection
                      roofType={roofType}
                      subType={subType}
                      token={token}
                      onUpdate={fetchCalculators}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 mt-4"
                    onClick={() => setConfirmDeleteSubtype({
                      roofTypeId: roofType.id,
                      subtypeName: subType.name
                    })}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}

              {(!roofType.sub_types || roofType.sub_types.length === 0) && (
                <div className="text-center py-8 text-slate-400">
                  Nu există subtipuri pentru acest tip de acoperiș.
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      <ConfirmDialog
        open={!!confirmDeleteSubtype}
        onClose={() => setConfirmDeleteSubtype(null)}
        onConfirm={handleDeleteSubtype}
        title="Șterge Subtip Calculator"
        message={`Sigur doriți să ștergeți subtipul "${confirmDeleteSubtype?.subtypeName}"? Toate etapele și materialele asociate vor fi de asemenea șterse.`}
      />

      <ConfirmDialog
        open={!!confirmDeleteRoofType}
        onClose={() => setConfirmDeleteRoofType(null)}
        onConfirm={handleDeleteRoofType}
        title="Șterge Calculator"
        message={`Sigur doriți să ștergeți calculatorul "${confirmDeleteRoofType?.name}"? Toate subtipurile, etapele și materialele asociate vor fi de asemenea șterse.`}
      />
    </div>
  );
};

export default CalculatorManager;
